/**
 * Password Validation Utility (Frontend)
 * 
 * Provides password strength validation and complexity checking
 * Matches backend validation rules
 */

/**
 * Check password complexity requirements
 * @param {string} password - Password to check
 * @returns {Object} - Complexity check results
 */
export const checkPasswordComplexity = (password) => {
  if (!password || typeof password !== 'string') {
    return {
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecialChar: false,
      complexityCount: 0
    }
  }

  const hasUppercase = /[A-Z]/.test(password)
  const hasLowercase = /[a-z]/.test(password)
  const hasNumber = /[0-9]/.test(password)
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)

  const complexityCount = [hasUppercase, hasLowercase, hasNumber, hasSpecialChar]
    .filter(Boolean).length

  return {
    hasUppercase,
    hasLowercase,
    hasNumber,
    hasSpecialChar,
    complexityCount
  }
}

/**
 * Get password strength level
 * @param {string} password - Password to evaluate
 * @returns {Object} - Strength information
 */
export const getPasswordStrength = (password) => {
  if (!password) {
    return {
      level: 'weak',
      score: 0,
      feedback: ['Le mot de passe est requis']
    }
  }

  const length = password.length
  const complexity = checkPasswordComplexity(password)

  let score = 0
  const feedback = []

  // Length scoring
  if (length >= 8) score += 1
  else feedback.push('Au moins 8 caractères requis')
  
  if (length >= 12) score += 1
  if (length >= 16) score += 1

  // Complexity scoring (need at least 3 of 4 types)
  if (complexity.complexityCount >= 3) score += 2
  else if (complexity.complexityCount >= 2) score += 1
  else {
    feedback.push('Au moins 3 types de caractères requis')
  }

  // Determine level
  let level = 'weak'
  if (score >= 4) level = 'strong'
  else if (score >= 2) level = 'medium'

  // Add specific feedback
  if (!complexity.hasUppercase) feedback.push('Ajouter des majuscules')
  if (!complexity.hasLowercase) feedback.push('Ajouter des minuscules')
  if (!complexity.hasNumber) feedback.push('Ajouter des chiffres')
  if (!complexity.hasSpecialChar) feedback.push('Ajouter des caractères spéciaux (!@#$%^&*)')

  if (feedback.length === 0) {
    feedback.push('Mot de passe fort')
  }

  return {
    level,
    score,
    feedback,
    complexity
  }
}

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {Object} - Validation result
 */
export const validatePasswordStrength = (password) => {
  if (!password) {
    return {
      valid: false,
      errors: ['Le mot de passe est requis']
    }
  }

  const errors = []
  const length = password.length
  const complexity = checkPasswordComplexity(password)

  // Minimum length check
  if (length < 8) {
    errors.push('Le mot de passe doit contenir au moins 8 caractères')
  }

  // Complexity check (at least 3 of 4 types)
  if (complexity.complexityCount < 3) {
    errors.push('Le mot de passe doit contenir au moins 3 types de caractères parmi: majuscules, minuscules, chiffres, caractères spéciaux')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Check if passwords match
 * @param {string} password - Password
 * @param {string} confirmPassword - Confirmation password
 * @returns {Object} - Match result
 */
export const checkPasswordMatch = (password, confirmPassword) => {
  if (!password || !confirmPassword) {
    return {
      match: false,
      error: 'Les deux mots de passe sont requis'
    }
  }

  if (password !== confirmPassword) {
    return {
      match: false,
      error: 'Les mots de passe ne correspondent pas'
    }
  }

  return {
    match: true,
    error: null
  }
}

export default {
  checkPasswordComplexity,
  getPasswordStrength,
  validatePasswordStrength,
  checkPasswordMatch
}

